import type { IsoDate, Period } from './dates';
import type { Recipe } from './recipe';

export interface MeatAddOn {
  memberId: string;
  productId: string;
  quantity: number;
}

export interface PlannedDinner {
  recipeId: string;
  /**
   * DE: Portionen für Abendessen und Mittagessen am nächsten Tag zusammen.
   * EN: Portions for dinner and the next day's lunch together.
   */
  servings: number;
  meatAddOns: MeatAddOn[];
  locked: boolean;
}

/**
 * DE: Ein Tag im Plan; das Mittagessen ist immer das Abendessen vom Vortag.
 * EN: One day of the plan; lunch is always the previous day's dinner.
 */
export interface PlannedDay {
  date: IsoDate;
  breakfast: { recipeId: string; servings: number };
  dinner: PlannedDinner;
}

export interface PlanWarning {
  code: 'relaxed-quick' | 'relaxed-lunchbox' | 'relaxed-variety' | 'over-budget' | 'shops-closed';
  date?: IsoDate;
  amount?: number;
  productIds?: string[];
}

export interface Plan {
  period: Period;
  days: PlannedDay[];
  warnings: PlanWarning[];
}

export interface Requirement {
  date: IsoDate;
  productId: string;
  quantity: number;
}

export interface RequirementOptions {
  staples: readonly string[];
  /**
   * DE: Zusätzliche Rezepte (z. B. Notfallvorrat), je einmal in Grundportionen.
   * EN: Extra recipes (e.g. the backup kit), once each in base servings.
   */
  extra?: readonly { date: IsoDate; recipeId: string }[];
}

function recipeOf(recipes: readonly Recipe[], id: string): Recipe {
  const recipe = recipes.find((candidate) => candidate.id === id);
  if (!recipe) throw new Error(`Unknown recipe: ${id}`);
  return recipe;
}

function recipeNeeds(recipe: Recipe, servings: number, date: IsoDate): Requirement[] {
  const factor = servings / recipe.baseServings;
  return recipe.ingredients.map((ingredient) => ({
    date,
    productId: ingredient.productId,
    quantity: ingredient.quantity * factor,
  }));
}

function mergeRequirements(needs: readonly Requirement[]): Requirement[] {
  const merged = new Map<string, Requirement>();
  for (const need of needs) {
    const key = `${need.date}|${need.productId}`;
    const quantity = (merged.get(key)?.quantity ?? 0) + need.quantity;
    merged.set(key, { ...need, quantity });
  }
  return [...merged.values()].sort(
    (a, b) => a.date.localeCompare(b.date) || a.productId.localeCompare(b.productId),
  );
}

/**
 * DE: Benötigte Mengen pro Tag und Produkt (ohne Grundzutaten), Grundlage für den Einkauf.
 * EN: Required quantities per day and product (without staples), the basis for shopping.
 */
export function planRequirements(
  plan: Plan,
  recipes: readonly Recipe[],
  options: RequirementOptions,
): Requirement[] {
  const needs: Requirement[] = [];
  for (const day of plan.days) {
    const { breakfast, dinner } = day;
    needs.push(...recipeNeeds(recipeOf(recipes, breakfast.recipeId), breakfast.servings, day.date));
    needs.push(...recipeNeeds(recipeOf(recipes, dinner.recipeId), dinner.servings, day.date));
    needs.push(
      ...dinner.meatAddOns.map(({ productId, quantity }) => ({
        date: day.date,
        productId,
        quantity,
      })),
    );
  }
  for (const { date, recipeId } of options.extra ?? []) {
    const recipe = recipeOf(recipes, recipeId);
    needs.push(...recipeNeeds(recipe, recipe.baseServings, date));
  }
  return mergeRequirements(needs.filter((need) => !options.staples.includes(need.productId)));
}
